import React, { useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import ContextConsumer from '../Context'
import QuickBet from './QuickBet'
import SnackBar from './SnackBar'
import FullScreenDialog from './FullScreenDialog'
import {
  removeFromSlip,
  updateOrder,
  updateUserBalance,
  addToBetHistory,
  getReceiptNumber,
} from './utilities.js'

// 3. Toggle button on snackbar to toggle between full betslip and snackbar
// 5. Add bet placement
// 6. Add loading state

// Move quickbet logic for counting up to mobile betslip
// just use context state rather than hooks i.e add open state to global state
// put the betslip together

const MobileBetslip = () => {
  // Need some state to hold onto the snackbar
  // when removing multi legs and one leg remains
  const [hold, setHold] = useState(false)
  const [fullSlip, setFullSlip] = useState(false)

  // Some local state to handle messages for the success/error component
  const [messageHub, setMessages] = useState({
    open: false,
    message: '',
    status: '',
    loading: false,
    successfulBet: {},
  })
  // Some local state to handle multi bets. Currently only set up
  // for straight multi bets
  const [multiBets, setMultiBets] = useState({
    straight: {},
  })

  // create a timer variable to simulate loading
  const timer = useRef()

  // Cleanup the timer used for simulated loading
  useEffect(
    () => () => {
      clearTimeout(timer.current)
    },
    []
  )

  // Click handlers to switch into full betslip mode
  const handleClickOpen = () => {
    setFullSlip(true)
  }

  const handleClose = () => {
    setFullSlip(false)
  }

  // Use this to hide the quickbet panel without immediately
  // changing its contents. It will avoid the flash of empty
  // fields. The reset handler is set to be called when the
  // the quickbet exit animation ends
  const dismissHandler = () => {
    setMessages({
      ...messageHub,
      open: false,
    })
  }

  // Use this for a straight up reset
  const resetHandler = () => {
    setMessages({
      ...messageHub,
      open: false,
      message: '',
      status: '',
      loading: false,
      successfulBet: {},
    })
  }

  return (
    <ContextConsumer>
      {({ data, set }) => {
        const betslipIds = Object.keys(data.betslip)
        const count = betslipIds.length

        // Will need some check here to see if we are
        // reversing out of a multi
        const isSnackBarOpen = count > 1 || (count >= 1 && hold)
        const isQuickBetOpen =
          (count === 1 && !isSnackBarOpen) ||
          messageHub.open ||
          messageHub.loading

        // Calculate multi price
        const totalOdds = betslipIds.reduce((total, key) => {
          const currentBet = data.betslip[key]
          return parseFloat(total + currentBet.odds)
        }, 0)

        // Check how many of the bets are from a unique event
        // so we can alter display for conflicting legs
        const validMultiLegs = _.uniq(
          Object.keys(data.betslip).map(key => data.betslip[key].id)
        )

        // Get the first bet for the QuickBet component
        const betsArray = betslipIds.map(key => {
          const firstBet = data.betslip[key]
          return firstBet
        })

        // We only need the first bet in the betsArray
        // for the QuickBet component
        const bet = betsArray.length ? betsArray[0] : {}

        // We'll need to know if the user is logged in
        // and their account balance
        const isUserLoggedIn = !_.isEmpty(data.user)
        const userBalance = parseFloat(data.user.balance)

        // Handle placing a bet
        const handlePlaceBet = totalStake => {
          // Replace this with a case statement of different scenarios
          // such as price has changed

          if (totalStake <= userBalance) {
            // Update messageHub state via a loading simulation
            setMessages({
              ...messageHub,
              loading: true,
            })
            // run a timer to simulate some loading
            timer.current = setTimeout(() => {
              const betPlaced = addToBetHistory(
                betslipIds,
                data,
                set,
                multiBets,
                setMultiBets
              )

              setMessages({
                ...messageHub,
                open: true,
                message: `Bet Placed. Receipt #${getReceiptNumber()}`,
                status: 'success',
                loading: false,
                successfulBet: betPlaced,
              })
            }, 2000)

            // Update the user's balance
            updateUserBalance(data, set, totalStake)
          } else {
            // Update messageHub state via a loading simulation
            setMessages({
              ...messageHub,
              loading: true,
            })
            // run a timer to simulate some loading
            timer.current = setTimeout(() => {
              setMessages({
                ...messageHub,
                open: true,
                message: 'Insufficient funds. Please amend stake or deposit',
                status: 'warning',
                loading: false,
              })
            }, 2000)
          }
        }

        const handleRemoveBet = (id, marketIndex, buttonIndex) => {
          removeFromSlip(data, set, id, marketIndex, buttonIndex)
          dismissHandler()
        }

        // Adjust the padding bottom of the body when quickbet and snackbar
        // are open. This will ensure users can scroll to the bottom of the page
        // The <scrollAllowance> component will read the value from state

        const setScrollAllowance = (componentHeight, allowForBottomBar) => {
          const defaultBottomBarHeight = 56
          const height = allowForBottomBar
            ? componentHeight + defaultBottomBarHeight
            : componentHeight
          set({ scrollAllowance: height })
        }

        return (
          <>
            <QuickBet
              isOpen={isQuickBetOpen}
              bet={bet}
              setHold={setHold}
              inputHandler={stake =>
                updateOrder(
                  data,
                  set,
                  bet.id,
                  bet.marketIndex,
                  bet.buttonIndex,
                  bet.odds,
                  stake
                )
              }
              removeBet={() =>
                handleRemoveBet(bet.id, bet.marketIndex, bet.buttonIndex)
              }
              clickHandler={stake =>
                updateOrder(
                  data,
                  set,
                  bet.id,
                  bet.marketIndex,
                  bet.buttonIndex,
                  bet.odds,
                  stake
                )
              }
              handlePlaceBet={handlePlaceBet}
              isUserLoggedIn={isUserLoggedIn}
              messageHub={messageHub}
              setMessages={setMessages}
              dismissHandler={dismissHandler}
              resetHandler={resetHandler}
              setScrollAllowance={setScrollAllowance}
            />
            <SnackBar
              isOpen={isSnackBarOpen}
              count={count}
              validMultiLegs={validMultiLegs}
              totalOdds={totalOdds}
              bet={bet}
              setHold={setHold}
              hold={hold}
              setFullSlip={setFullSlip}
              setScrollAllowance={setScrollAllowance}
            />
            <FullScreenDialog handleClose={handleClose} open={fullSlip} />
          </>
        )
      }}
    </ContextConsumer>
  )
}

export default MobileBetslip
